import { FileExcelOutlined, FileTextOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, Tooltip, message, Button, Space } from 'antd';
import { useMutation } from '@tanstack/react-query';
import React, { useState, type ReactNode } from 'react';
import { saveAs } from "file-saver";
import { post } from "../services/httpClient"; // <-- normal post 
import ExcelJS from 'exceljs';
import { t } from 'i18next';


interface ExportProps {
    apiUrl: string;
    iconExcel?: ReactNode;
    iconCsv?: ReactNode;
    confirmButtonText?: string;
    defaultPayload?: Record<string, any>;
    extraPayload?: Record<string, any>;
    fileName?: string;
    fileType?: "excel" | "csv";
    columns?: { header: string; key: string; transform?: (val: any) => any }[];
    compact?: boolean; 
}

const Export: React.FC<ExportProps> = ({
    apiUrl,
    iconExcel = <FileExcelOutlined />,
    iconCsv = <FileTextOutlined />,
    confirmButtonText,
    defaultPayload = {},
    extraPayload = {},
    fileName = "Export",
    fileType = "excel",
    columns = [],
    compact = false
}) => {
    const [isLoading, setIsLoading] = useState(false);

    const mutation = useMutation({
        mutationFn: async (_exportType: number) => {
            setIsLoading(true);
            const finalPayload = { ...defaultPayload, ...extraPayload, IsExport: true, PageSize: 0, PageNumber: 0 };

            // now here json data will fetch 
            const response = await post<{ data: any[] }>(apiUrl, finalPayload);
            return response.data;
        },
        onSuccess: async (data, _exportType) => {
            setIsLoading(false);

            if (!data || data.length === 0) {
                message.error(t("No data found to export."));
                return;
            }

            // Map columns
            const mappedData = (data || []).map((row) => {
                const mapped: Record<string, any> = {};
                columns.forEach((col) => {
                    const value = row[col.key];
                    mapped[col.header] = col.transform ? col.transform(value) : value;
                });
                return mapped;
            });

            if (fileType === "csv") {
                // CSV export
                const csvHeaders = columns.map(c => c.header).join(",");
                const csvRows = mappedData.map(row =>
                    columns.map(c => `"${row[c.header] ?? ''}"`).join(",")
                );
                const csvContent = [csvHeaders, ...csvRows].join("\n");
                saveAs(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }), `${fileName}.csv`);
            } else {
                // Excel export
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Sheet1');

                const headerRow = worksheet.addRow(columns.map(c => c.header));
                headerRow.eachCell(cell => {
                    cell.font = { bold: true, color: { argb: 'FF000000' } };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                });

                mappedData.forEach(d => worksheet.addRow(columns.map(c => d[c.header])));

                           
                // Auto width
                worksheet.columns?.forEach(column => {
                    if (!column) return; // extra safety
                    let maxLength = 10;
                    column.eachCell?.({ includeEmpty: true }, cell => {
                        const val = cell.value ? cell.value.toString() : '';
                        if (val.length > maxLength) maxLength = val.length;
                    });
                    column.width = maxLength + 2;
                });

                // Write buffer & save
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), `${fileName}.xlsx`);
            }
            message.success(t('Data export successful!'));


        },
        onError: (error: any) => {
            setIsLoading(false);
            message.error(error.message || t("Failed to export file."));
        },
    });


    const showConfirm = (exportType: number) => {
        Modal.confirm({
            title: confirmButtonText ?? t('Are you sure you want to export?'),
            icon: <ExclamationCircleOutlined />,
            okText: t('Yes'),
            okType: 'primary',
            cancelText: t('No'),
            onOk: () => mutation.mutateAsync(exportType),
        });
    };

    return (
        <Space>
            <Tooltip title={t('Export to ') + t(`${fileType}`)}>
                <Button
                    size="large"
                    style={{ marginBottom: compact ? 0 : 16, backgroundColor: '#e6f7ff', borderColor: '#91d5ff', color: '#096dd9' }}
                    icon={fileType === "excel" ? iconExcel : iconCsv}
                    onClick={(e) => {
                        e.stopPropagation();
                        const exportType = fileType === "excel" ? 0 : 1;
                        showConfirm(exportType);
                    }}
                    loading={isLoading}
                >
                    {t('Export to ') + t(`${fileType}`)}
                </Button>
            </Tooltip>
        </Space>
    );
};

export default Export;
